// Per-puzzle TRACE for the rebus game — one create-only Firestore doc per attempt, written
// as each puzzle finishes. Shares the capture stream types; `domain: 'rebus'` distinguishes it
// in the single `labsTraces` collection (BQ `traces_raw_latest` filters on it).

import { TranscriptItem, InteractionEvent, CaptureMode, StopRule } from '../../../shared/labs/types'
import { CaptureEnv } from '../../../shared/lib/env'

export type RebusAnswerMode = 'typed' | 'voice-detected' | 'pass' | 'timeout' | null

export type RebusTrace = {
  schemaVersion: number
  domain: 'rebus'
  // grouping / session
  sessionId: string
  mode: CaptureMode
  trialIndex: number
  sitting: number
  resumed: boolean
  aborted: boolean
  // puzzle identity + difficulty
  puzzleId: string // e.g. "10_3"
  imageUrl: string
  answer: string // canonical answer (offline scoring; never shown pre-commit)
  llmSolveRate: number // mean LLM correctness (0..1) — difficulty axis + human-vs-LLM anchor
  band: number | null // test-mode difficulty band; null in free play
  conditionId: number | null // pre-determined test-group id (24-game model); null for band-draw / free play
  // the trace itself
  transcript: TranscriptItem[]
  events: InteractionEvent[]
  committedTranscript: string
  sttEngine: 'webspeech' | 'deepgram' | 'google'
  // answer + correctness
  answerMode: RebusAnswerMode
  typedAnswer: string | null
  matchedAnswer: string | null
  correct: boolean
  correctnessReason: string
  // timing
  puzzleStartedAt: string // ISO wall clock
  // On a HELD trial this runs to the end of the forced window, not to the moment the participant
  // gave up, so it is the exposure the trial actually had. Clamped at the 3-minute cap.
  puzzleDurationMs: number
  reflectionDurationMs: number // commit → answer entry (covers the retro when one was shown)
  // ---- the forced-stop arm (schema 8). See analysis/human24/PREREG_stoprule_rebus.md ----
  // 'free' is the protocol to date: a participant ends an unsolved trial whenever they choose.
  // 'forced' hides the give-up control and holds a failed trial to the 3-minute cap, which is the
  // cap that already applied either way. Drawn once per SESSION at consent, so it covers the
  // practice puzzles too: showing the control in practice and withdrawing it at test start would
  // be a behaviour change mid-session. null on free play before the field existed.
  stopRule: StopRule | null
  // How long the held window stayed open, absent when the trial was never held. Its presence is
  // what distinguishes a held trial from an ordinary one in the forced arm.
  forcedWindowMs: number | null
  // instant-solve retrospective (aha payoff) — only populated when the solve was trivial
  // (fast / near-wordless; see trivialSolve.ts), which swaps the passive reflection for an active
  // untimed retro. The retro's spoken decode lives IN `transcript` in-stream (like the reflection),
  // bracketed by the retro_start/retro_end events; these fields carry what the transcript can't.
  trivialSolve: boolean
  solutionType: 'insight' | 'analytic' | null // self-reported: did it click, or was it worked out?
  // environment + consent
  env: CaptureEnv
  consented: boolean
  source: { src: string | null; token: string | null }
  // Reveal-mechanic A/B (PREREG_reveal_ab). `revealArm` is the sticky per-user ASSIGNMENT;
  // `revealRegime` is what this trace actually ran under. They differ while arm B is live in
  // some games but not others, which is what makes mixed-exposure users detectable.
  revealArm: 'A' | 'B'
  revealRegime: 'A' | 'B'
  revealThreshold: number // REVEAL_A_SHARE in force at assignment; makes reassignment auditable
  revealForced: boolean // assignment came from ?reveal= (QA traffic — exclude from analysis)
  // Device-local participant covariates (see shared/labs/nerdleSkill.ts). Coarse bands only,
  // no account and no id; cut-points are shared with the Targets capture so a band means the
  // same thing on both surfaces. `null` when unmeasurable — never 0 or -1.
  skillBand: number | null
  tenureBand: number | null
  statsAgeBand: string | null
  statsSource: string
  g24Prior: string
  targetsExposure: string
  firstExposure: boolean
  // Which deployed bundle produced this trace (see shared/labs/build.ts). Split
  // before/after comparisons on THIS, not on the timestamp — CDN cache lag means
  // the two disagree.
  labsBuild: string
}

// v3: pre-determined test conditions (24-game model) — `conditionId` set on test traces; the
// prior random-per-band cohort is v2 (retained + selectable by schemaVersion < 3).
// v4: reveal-mechanic A/B fields (see shared/labs/revealArm.ts).
// v10: `lastGame` — one classic-nerdle game (guesses + per-guess timings) read from
// localStorage, for the within-puzzle skill norm. NULL on every trace until the classic
// app deploy that writes it, and null thereafter for anyone whose device has not played
// classic. Do not read a null as 'unskilled'.
// v8: the forced-stop arm. `stopRule` and `forcedWindowMs`, drawn at consent so the practice
// puzzles are covered too. See analysis/human24/PREREG_stoprule_rebus.md.
export const REBUS_TRACE_SCHEMA_VERSION = 8
